import React, { useEffect, useState } from "react";

// Normalises what someone is likely to paste: with or without the hash,
// three digits or six, any case. Returns a full 6-digit hex or null.
export const normalizeHex = (raw) => {
  const v = String(raw || "").trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(v)) {
    return `#${v[0]}${v[0]}${v[1]}${v[1]}${v[2]}${v[2]}`.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`.toLowerCase();
  return null;
};

// A swatch picker paired with the hex it represents, so a value can be
// pasted in rather than hunted for in the picker.
//
// The text box holds a local draft while it is being typed: committing on
// every keystroke would fight the user, since "#FF7" is a valid 3-digit hex
// on the way to "#FF7AAC". It commits on a complete value, on blur, and on
// Enter, and snaps back to the real value if what is there cannot parse.
const ColorField = ({ value, onChange, disabled }) => {
  const [draft, setDraft] = useState(value || "");

  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = (raw) => {
    const hex = normalizeHex(raw);
    if (hex) onChange(hex);
    else setDraft(value || "");
  };

  return (
    <div className="mt-1 flex items-stretch gap-2">
      <input
        type="color"
        value={normalizeHex(value) || "#000000"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-12 flex-none border-[2px] border-black bg-white cursor-pointer"
        aria-label="Colour picker"
      />
      <input
        type="text"
        value={draft}
        disabled={disabled}
        spellCheck="false"
        placeholder="#000000"
        onChange={(e) => {
          setDraft(e.target.value);
          // Commit as soon as it is a complete colour, so the picker and
          // any preview keep up with a paste without waiting for blur.
          if (normalizeHex(e.target.value)) onChange(normalizeHex(e.target.value));
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
        className="h-10 flex-1 min-w-0 px-2 font-mono text-xs uppercase border-[2px] border-black bg-white focus:outline-none focus:ring-2 focus:ring-ssblue"
        aria-label="Hex colour value"
      />
    </div>
  );
};

export { ColorField };
export default ColorField;
